import type { Theme } from '@shared/schema/theme'
import { FONT_STACKS } from './fonts'

/**
 * ใส่ค่าธีมเป็น CSS variables ลงบน element ที่กำหนด (ไม่ใช่ :root)
 * ทำให้ธีมของ flow ไม่รั่วไปเปลี่ยนหน้าตาของหน้า builder และพรีวิวหลายธีมพร้อมกันได้
 */

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const value = hex.replace('#', '')
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  }
}

/** เลือกสีตัวอักษรบนพื้นสีหลักให้อ่านออกเสมอ */
export function contrastColor(hex: string): string {
  const { r, g, b } = hexToRgb(hex)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#17181c' : '#ffffff'
}

/** สีอ่อนของสีหลัก ใช้เป็นพื้นหลังของสถานะที่ถูกเลือก */
export function softColor(hex: string, amount = 0.92): string {
  const { r, g, b } = hexToRgb(hex)
  const blend = (channel: number) => Math.round(channel + (255 - channel) * amount)
  return `rgb(${blend(r)}, ${blend(g)}, ${blend(b)})`
}

export type ThemeVars = Record<string, string>

export function themeToVars(theme: Theme): ThemeVars {
  const vars: ThemeVars = {
    '--fs-primary': theme.primaryColor,
    '--fs-primary-soft': softColor(theme.primaryColor),
    '--fs-on-primary': contrastColor(theme.primaryColor),
    '--fs-font': FONT_STACKS[theme.fontId],
    // ไม่ตั้งค่าสีแถบความคืบหน้าเอง → ใช้สีหลักตามเดิม (ค่าเริ่มต้นเดิมของระบบ)
    '--fs-progress': theme.progressColor ?? theme.primaryColor,
  }
  if (theme.backgroundColor) {
    vars['--fs-bg'] = theme.backgroundColor
    vars['--fs-on-bg'] = contrastColor(theme.backgroundColor)
  }
  return vars
}

export function applyTheme(theme: Theme, element: HTMLElement): void {
  for (const [key, value] of Object.entries(themeToVars(theme))) {
    element.style.setProperty(key, value)
  }
}

/** ใช้กับ style prop ของ React ได้โดยตรง */
export function themeStyle(theme: Theme): React.CSSProperties {
  return { ...themeToVars(theme), fontFamily: 'var(--fs-font)' } as React.CSSProperties
}
